/**
 * Frames in their own desktop windows: a focused frame's window dragged
 * toward another display arms the global floating-frame drag, which takes
 * the window over once the pointer or the window crosses the display edge
 * (the canvas focus node drag and the focus-over-the-wall header drag); a
 * card Denker shows from another project opens in one at once.
 */
import type { Frame } from "@/api/spaces";
import type { ClientPoint } from "@/components/focus/frame-window-shell";
import type { FocusWindowBox } from "@/lib/focus-window-layout";
import { frameDataFromApi, type FrameData } from "@/lib/frame-data";
import {
  noteCardWindow,
  type CardPresenter,
} from "@/lib/focus/agent-presented-cards";
import { isTauri } from "@/lib/is-tauri";
import { usePresentationStore } from "@/stores/presentation-store";
import { useProjectStore } from "@/stores/project-store";
import { getFloatingFrameDisplayArea } from "./floating-frame-displays";
import { beginFloatingFrameDrag } from "./floating-frame-drag";
import {
  nextFloatingFrameOrder,
  putFloatingFrame,
  raiseFloatingFrame,
  useFloatingFrameStore,
} from "./floating-frame-store";
import { getWindowDisplayId } from "./use-window-role";
import { getWindowOrigin } from "./use-window-origin";

interface FocusWindowTransfer {
  frame: FrameData;
  /** The window's box in its surface's coordinates. */
  box: FocusWindowBox;
  /** The surface's top-left corner in client pixels. */
  surfaceOrigin: { x: number; y: number };
  press: ClientPoint;
}

export function beginFocusWindowTransfer({
  frame,
  box,
  surfaceOrigin,
  press,
}: FocusWindowTransfer): void {
  const { presentation } = usePresentationStore.getState();
  const { currentProject, currentSpace } = useProjectStore.getState();
  const displayId = getWindowDisplayId();
  if (
    !isTauri() ||
    displayId === null ||
    !currentProject ||
    !currentSpace ||
    presentation?.variant !== "focus" ||
    !presentation.frameIds.includes(frame.frameId)
  )
    return;
  const windowOrigin = getWindowOrigin();
  const toGlobal = (local: FocusWindowBox): FocusWindowBox => ({
    ...local,
    x: local.x + surfaceOrigin.x + windowOrigin.x,
    y: local.y + surfaceOrigin.y + windowOrigin.y,
  });
  const managed =
    presentation.focusWindow?.frameId === frame.frameId
      ? presentation.focusWindow
      : undefined;
  const globalBox = toGlobal(box);
  beginFloatingFrameDrag(
    {
      frameId: frame.frameId,
      frame,
      projectId: currentProject.id,
      spaceId: currentSpace.id,
      displayId,
      sourceDisplayId: displayId,
      box: globalBox,
      normalBox: managed ? toGlobal(managed.normalBox) : globalBox,
      mode: managed?.mode ?? "normal",
      dragging: true,
    },
    press,
    { waitForCrossing: true },
  );
}

/**
 * A card Denker showed from another of the user's projects opens at once in
 * its own window, centred on this display, without switching the view or
 * joining the wall. Agent-opened: it takes no keyboard, and its presenter's
 * next guide `show_only` closes it. False where there are no own windows
 * (the web app).
 */
export function openOtherProjectCardWindow(
  card: Frame,
  presenter: CardPresenter,
): boolean {
  const displayId = getWindowDisplayId();
  if (!isTauri() || displayId === null || !card.project) return false;
  noteCardWindow(presenter, card.id);
  // Shown again (a replay): the window keeps where the user put it.
  if (useFloatingFrameStore.getState().frames[card.id]) {
    raiseFloatingFrame(card.id);
    return true;
  }
  const origin = getWindowOrigin();
  const area = getFloatingFrameDisplayArea(displayId) ?? {
    x: origin.x,
    y: origin.y,
    width: window.innerWidth,
    height: window.innerHeight,
  };
  const width = Math.min(card.width, area.width);
  const height = Math.min(card.height, area.height);
  const box: FocusWindowBox = {
    x: area.x + (area.width - width) / 2,
    y: area.y + (area.height - height) / 2,
    width,
    height,
  };
  putFloatingFrame({
    frameId: card.id,
    frame: frameDataFromApi(card),
    projectId: card.project,
    spaceId: card.space ?? card.project,
    box,
    normalBox: box,
    displayId,
    sourceDisplayId: displayId,
    dragging: false,
    order: nextFloatingFrameOrder(),
    mode: "normal",
    otherProject: true,
  });
  return true;
}
